import React, { useCallback, useEffect, useState } from 'react'
import { Col, Row } from 'react-bootstrap'
import { formatNumber, formatValueByUnit } from '@utils/index'
import { DashboardUnits, LegendData } from '@dataTypes/dashboard.types'
import DashboardSlider from '../DashboardSlider.component'
import ProgressBar from '../Charts/ProgressBar.component'
import ProgressBarFooter from './ProgressBarFooter.components'

import '../DashboardStyle.css'

type ProgressBarCardContentProps = {
  title: string
  data: any
  legendData?: LegendData[]
  progUnit: DashboardUnits
  classNameCol?: string
  progLabel?: string
  shouldShowSlider?: boolean
  shouldDisplayFooter?: boolean
  footerData?: any
  progressBarHeight?: string
  progressBarWidth?: string
  iconLabel?: string
  classNameTextHeader?: string
  displayPercent?: boolean
}

function ProgressBarCardContent({
  title,
  data,
  progLabel,
  progUnit,
  shouldShowSlider,
  shouldDisplayFooter,
  progressBarHeight,
  progressBarWidth,
  iconLabel,
  classNameTextHeader,
  legendData,
  classNameCol,
  displayPercent,
}: ProgressBarCardContentProps): React.ReactElement {
  const [diagnosticSlider, setDiagnosticSlider] = useState(
    data?.diagnostic?.actual,
  )
  const [acceptanceSlider, setAcceptanceSlider] = useState(
    data?.acceptance?.actual,
  )
  const [progressBarActual, setProgressBarActual] = useState(0)

  const updateProgress = useCallback((): void => {
    const totalActual = diagnosticSlider + acceptanceSlider
    setProgressBarActual(totalActual)
  }, [diagnosticSlider, acceptanceSlider])

  const handleSliderDiagnostic = (event: any): void => {
    setDiagnosticSlider(event.value)
    updateProgress()
  }

  const handleSliderAcceptance = (event: any): void => {
    setAcceptanceSlider(event.value)
    updateProgress()
  }

  useEffect(() => {
    updateProgress()
  }, [updateProgress])

  return (
    <Row className="text-center">
      <Col
        md={12}
        className={`fw-semibold dashboard-text-color ${classNameTextHeader}`}
      >
        {progLabel && `${progLabel}`}
        <span>{iconLabel && <i className={iconLabel}></i>}</span>&nbsp;
        <span>{`${formatValueByUnit(data?.goal, progUnit)}`}</span>
      </Col>
      <Col md={12}>
        <ProgressBar
          title={title}
          goal={data.goal}
          actual={shouldShowSlider ? progressBarActual : data?.actual}
          unit={data?.unit}
          height={progressBarHeight}
          width={progressBarWidth}
          displayPercent={displayPercent}
        />
      </Col>
      <Col md={12} className="justify-content-center">
        <Row>
          {shouldShowSlider && (
            <Row>
              <Col md={6}>
                <DashboardSlider
                  title={'Restorative/Elective Case-Diagnostic'}
                  label="Diagnostic"
                  data={data.diagnostic}
                  toggleSlider={handleSliderDiagnostic}
                />
              </Col>
              <Col md={6}>
                <DashboardSlider
                  title={'Restorative/Elective Case-Acceptance'}
                  label="Acceptance"
                  data={data.acceptance}
                  toggleSlider={handleSliderAcceptance}
                />
              </Col>
            </Row>
          )}
          {shouldDisplayFooter && (
            <Col md={12}>
              <ProgressBarFooter title={title} />
            </Col>
          )}
          {legendData &&
            legendData.map(({ name, value, showCurrency }) => (
              <Col
                className={`${classNameCol ?? 'col-md-3'} mt-2`}
                key={`${name}`}
              >
                <div className="text-black">{name}</div>
                <div className="text-black fw-bolder">
                  {formatNumber(value, showCurrency)}
                </div>
              </Col>
            ))}
        </Row>
      </Col>
    </Row>
  )
}

export default ProgressBarCardContent
